import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { Badge } from './ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { MapPin, Navigation, Car, Users, Map, List, Layers, ChevronDown, ChevronUp, Building2, ParkingSquare, Tag, CreditCard, X, Heart, ArrowDownNarrowWide, Flag, ExternalLink, Footprints } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import MapView from './MapView';
import LocationDetailModal from './LocationDetailModal';
import ReservationModal from './ReservationModal';

// Haversine distance in miles
const haversineDistance = (lat1, lng1, lat2, lng2) => {
  const toRad = (d) => (d * Math.PI) / 180;
  const R = 3958.8; // Earth radius in miles
  const dLat = toRad(lat2 - lat1);
  const dLng = toRad(lng2 - lng1);
  const a = Math.sin(dLat / 2) ** 2 + Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLng / 2) ** 2;
  return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
};

const getGarageCoords = (coords) => {
  if (!coords) return null;
  if (Array.isArray(coords) && coords.length >= 2) return { lat: coords[0], lng: coords[1] };
  if (typeof coords === 'object' && coords.lat != null && coords.lng != null) return { lat: Number(coords.lat), lng: Number(coords.lng) };
  return null;
};

// Walk time in minutes (avg 3.1 mph, ×1.3 routing factor for streets)
const calcWalkMinutes = (lat1, lng1, lat2, lng2) => {
  const miles = haversineDistance(lat1, lng1, lat2, lng2) * 1.3;
  return Math.round((miles / 3.1) * 60);
};

const formatWalkTime = (mins) => {
  if (mins < 1) return '< 1 min';
  if (mins >= 60) return `${Math.floor(mins / 60)}h ${mins % 60}m`;
  return `${mins} min`;
};

const API_URL = process.env.REACT_APP_BACKEND_URL;

const getBarColor = (pct) => pct >= 90 ? 'bg-red-500' : pct >= 70 ? 'bg-yellow-500' : 'bg-green-500';
const getBarTextColor = (pct) => pct >= 90 ? 'text-red-600' : pct >= 70 ? 'text-yellow-600' : 'text-green-600';
const getAvailabilityColor = (s) => s === 'available' ? 'bg-green-100 text-green-800' : s === 'limited' ? 'bg-yellow-100 text-yellow-800' : s === 'full' ? 'bg-red-100 text-red-800' : 'bg-gray-100 text-gray-800';
const getAvailabilityText = (s) => s === 'available' ? 'Available' : s === 'limited' ? 'Limited' : s === 'full' ? 'Full' : 'Unknown';

const hexToRgba = (hex, alpha) => {
  try {
    const r = parseInt(hex.slice(1, 3), 16);
    const g = parseInt(hex.slice(3, 5), 16);
    const b = parseInt(hex.slice(5, 7), 16);
    return `rgba(${r},${g},${b},${alpha})`;
  } catch { return `rgba(59,130,246,${alpha})`; }
};

export default function PublicViewer() {
  const { slug } = useParams();
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeView, setActiveView] = useState('map');
  const [searchTerm, setSearchTerm] = useState('');
  const [typeFilter, setTypeFilter] = useState('all');
  const [selectedGarage, setSelectedGarage] = useState(null);
  const [hoveredGarageId, setHoveredGarageId] = useState(null);
  const [tagFilter, setTagFilter] = useState('all');
  const [reservationGarage, setReservationGarage] = useState(null);
  const [mobilePanelOpen, setMobilePanelOpen] = useState(false);
  const [locationStatus, setLocationStatus] = useState('idle');
  const [userLocation, setUserLocation] = useState(null);
  const [sortNearest, setSortNearest] = useState(false);
  const [favorites, setFavorites] = useState(() => {
    try { return JSON.parse(localStorage.getItem(`parking-fav-${slug}`) || '[]'); } catch { return []; }
  });
  const [showFavoritesOnly, setShowFavoritesOnly] = useState(false);
  const [showDestinations, setShowDestinations] = useState(true);

  const toggleFavorite = useCallback((garageId) => {
    setFavorites(prev => {
      const next = prev.includes(garageId) ? prev.filter(id => id !== garageId) : [...prev, garageId];
      localStorage.setItem(`parking-fav-${slug}`, JSON.stringify(next));
      return next;
    });
  }, [slug]);

  const findUserLocation = () => {
    if (!("geolocation" in navigator)) return;
    setLocationStatus('loading');
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        setUserLocation({ lat: pos.coords.latitude, lng: pos.coords.longitude });
        setLocationStatus('found');
        setSortNearest(true);
      },
      () => { setLocationStatus('error'); },
      { enableHighAccuracy: true, timeout: 10000 }
    );
  };

  useEffect(() => {
    const fetchConfig = async () => {
      try {
        const res = await fetch(`${API_URL}/api/public/maps/${slug}`);
        if (!res.ok) throw new Error('Map not found');
        const data = await res.json();
        setConfig(data);
        if (data.branding?.showDestinations === false) setShowDestinations(false);
      } catch (e) {
        setError(e.message);
      } finally {
        setLoading(false);
      }
    };
    fetchConfig();
  }, [slug]);

  // Randomize availability numbers on each page load for demo effect
  const randomizeAvailability = (totalSpaces, attrs, levels) => {
    const occRate = 0.2 + Math.random() * 0.65;
    const occupied = Math.round(totalSpaces * occRate);
    const available = totalSpaces - occupied;

    const newAttrs = (attrs || []).map(a => {
      const t = a.totalSpaces || 0;
      const aOcc = Math.round(t * Math.max(0, Math.min(1, occRate + (Math.random() - 0.5) * 0.5)));
      return { ...a, occupiedSpaces: aOcc, availableSpaces: t - aOcc, utilizedPercent: t > 0 ? Math.round((aOcc / t) * 100) : 0 };
    }).filter(a => !(a.name === 'General' && a.totalSpaces === 0));

    const newLevels = (levels || []).map(l => {
      const t = l.totalSpaces || 0;
      const lOcc = Math.round(t * Math.max(0.05, Math.min(0.98, occRate + (Math.random() - 0.5) * 0.4)));
      return { ...l, occupiedSpaces: lOcc, availableSpaces: t - lOcc, utilizedPercent: t > 0 ? Math.round((lOcc / t) * 100) : 0 };
    });

    return { occupied, available, occRate, newAttrs, newLevels };
  };

  const garages = useMemo(() => {
    if (!config?.locations) return [];
    return config.locations.map(site => {
      const usage = site.latestSiteParkingUsages?.nodes?.[0];
      if (!usage) return null;
      const totalSpaces = usage.totalCount;
      const { occupied, available, occRate, newAttrs, newLevels } = randomizeAvailability(totalSpaces, site.attributes, site.levels);
      const utilizedPercent = Math.round(occRate * 100);
      return {
        id: site.id, name: site.displayName, address: site.address1,
        totalSpaces, occupiedSpaces: occupied,
        availableSpaces: available, utilizedPercent,
        availabilityStatus: utilizedPercent >= 90 ? 'full' : utilizedPercent >= 70 ? 'limited' : 'available',
        lastUpdated: new Date(), isLive: false, isFake: true,
        coordinates: site.coordinates, iconColor: site.iconColor,
        iconStyle: site.iconStyle, iconLabel: site.iconLabel,
        locationType: site.locationType, levels: newLevels,
        attributes: newAttrs, photoUrl: site.photoUrl,
      };
    }).filter(Boolean);
  }, [config]);

  // Parse destinations from config
  const projectDestinations = useMemo(() => {
    if (!config?.destinations) return [];
    return config.destinations.filter(d => d.coordinates);
  }, [config]);

  // Compute walk times from each garage to each destination
  const garageWalkTimes = useMemo(() => {
    if (!projectDestinations.length) return {};
    const result = {};
    garages.forEach(g => {
      const gc = getGarageCoords(g.coordinates);
      if (!gc) return;
      result[g.id] = projectDestinations.map(d => {
        const dc = getGarageCoords(d.coordinates);
        if (!dc) return { dest: d, walkMin: null };
        return { dest: d, walkMin: calcWalkMinutes(gc.lat, gc.lng, dc.lat, dc.lng) };
      }).filter(w => w.walkMin != null).sort((a, b) => a.walkMin - b.walkMin);
    });
    return result;
  }, [garages, projectDestinations]);

  // Collect all unique custom tag names across locations (from attributes, excluding "General")
  const allTags = useMemo(() => {
    const tagSet = new Set();
    garages.forEach(g => {
      (g.attributes || []).forEach(a => {
        if (a.name && a.name !== 'General') tagSet.add(a.name);
      });
    });
    return Array.from(tagSet).sort();
  }, [garages]);

  // Attach distance from user location to each garage
  const garagesWithDistance = useMemo(() => {
    if (!userLocation) return garages;
    return garages.map(g => {
      const c = getGarageCoords(g.coordinates);
      if (!c) return { ...g, distance: null };
      const dist = haversineDistance(userLocation.lat, userLocation.lng, c.lat, c.lng);
      return { ...g, distance: Math.round(dist * 10) / 10 };
    });
  }, [garages, userLocation]);

  const filteredGarages = useMemo(() => {
    let result = garagesWithDistance;
    if (typeFilter !== 'all') result = result.filter(g => g.locationType === typeFilter);
    if (tagFilter !== 'all') result = result.filter(g =>
      (g.attributes || []).some(a => a.name === tagFilter && a.totalSpaces > 0)
    );
    if (showFavoritesOnly) result = result.filter(g => favorites.includes(g.id));
    if (searchTerm) result = result.filter(g =>
      g.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      g.address.toLowerCase().includes(searchTerm.toLowerCase())
    );
    if (sortNearest && userLocation) {
      result = [...result].sort((a, b) => {
        if (a.distance == null && b.distance == null) return 0;
        if (a.distance == null) return 1;
        if (b.distance == null) return -1;
        return a.distance - b.distance;
      });
    }
    return result;
  }, [garagesWithDistance, searchTerm, typeFilter, tagFilter, showFavoritesOnly, favorites, sortNearest, userLocation]);

  const getDirectionsUrl = (g) => {
    const c = g.coordinates;
    if (c) {
      const lat = Array.isArray(c) ? c[0] : c.lat;
      const lng = Array.isArray(c) ? c[1] : c.lng;
      if (lat && lng) return `https://www.google.com/maps/dir/?api=1&destination=${lat},${lng}`;
    }
    return `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(g.address || '')}`;
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="w-10 h-10 border-4 border-blue-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (error || !config) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="text-center">
          <Car className="w-16 h-16 text-slate-400 mx-auto mb-4" />
          <h1 className="text-2xl font-bold text-slate-800 mb-2">Map Not Found</h1>
          <p className="text-slate-600">The parking map you're looking for doesn't exist.</p>
        </div>
      </div>
    );
  }

  const branding = config.branding || {};
  const primaryColor = branding.primaryColor || '#3b82f6';
  const textMode = branding.textMode || 'light';
  const showReservation = branding.showReservationDemo !== false;
  const surfaceCount = garages.filter(g => g.locationType === 'surface').length;
  const garageCount = garages.filter(g => g.locationType === 'garage').length;
  // Side panel garage list (shared between map view's side panel)
  const GarageListItem = ({ garage }) => {
    const isHovered = hoveredGarageId === garage.id;
    const isFav = favorites.includes(garage.id);
    return (
      <div
        className={`p-3 rounded-lg border cursor-pointer transition-all duration-150 ${
          isHovered ? 'bg-blue-50 border-blue-300 shadow-md scale-[1.01]' : 'bg-white border-slate-200 hover:border-slate-300'
        }`}
        onMouseEnter={() => setHoveredGarageId(garage.id)}
        onMouseLeave={() => setHoveredGarageId(null)}
        onClick={() => { setSelectedGarage(garage); setMobilePanelOpen(false); }}
        data-testid={`side-item-${garage.id}`}
      >
        <div className="flex items-start justify-between gap-2 mb-2">
          <div className="min-w-0 flex-1">
            <h4 className="text-sm font-semibold text-slate-800 truncate">{garage.name}</h4>
            <p className="text-[11px] text-slate-500 truncate">{garage.address}</p>
          </div>
          <div className="flex items-center gap-1.5 flex-shrink-0">
            <button
              onClick={(e) => { e.stopPropagation(); toggleFavorite(garage.id); }}
              className="p-1 rounded-full hover:bg-slate-100 transition-colors"
              data-testid={`fav-btn-${garage.id}`}
            >
              <Heart className={`w-3.5 h-3.5 transition-colors ${isFav ? 'fill-red-500 text-red-500' : 'text-slate-300 hover:text-red-400'}`} />
            </button>
            <Badge className={`${getAvailabilityColor(garage.availabilityStatus)} text-[10px]`}>
              {getAvailabilityText(garage.availabilityStatus)}
            </Badge>
          </div>
        </div>
        {/* Distance badge */}
        {garage.distance != null && (
          <div className="flex items-center gap-1 text-[11px] text-slate-500 mb-1.5">
            <Navigation className="w-3 h-3" />
            <span>{garage.distance < 0.1 ? '< 0.1' : garage.distance} mi away</span>
          </div>
        )}
        {/* Walk times to destinations */}
        {showDestinations && garageWalkTimes[garage.id]?.length > 0 && (
          <div className="flex flex-wrap gap-1 mb-1.5">
            {garageWalkTimes[garage.id].slice(0, 2).map(wt => (
              <span
                key={wt.dest.id}
                className="inline-flex items-center gap-0.5 text-[10px] bg-indigo-50 text-indigo-700 px-1.5 py-0.5 rounded-full"
              >
                <Footprints className="w-2.5 h-2.5" />
                {formatWalkTime(wt.walkMin)} to {wt.dest.name}
              </span>
            ))}
          </div>
        )}
        {/* Mini stats */}
        <div className="flex items-center justify-between text-xs mb-1.5">
          <span className="text-green-600 font-bold">{garage.availableSpaces} avail</span>
          <span className="text-slate-500">{garage.totalSpaces} total</span>
          <span className={`font-bold ${getBarTextColor(garage.utilizedPercent)}`}>{garage.utilizedPercent}%</span>
        </div>
        <div className="w-full bg-slate-200 rounded-full h-1.5">
          <div className={`h-1.5 rounded-full ${getBarColor(garage.utilizedPercent)}`} style={{ width: `${Math.max(garage.utilizedPercent, 2)}%` }} />
        </div>
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-slate-50" data-testid="public-viewer">
      {/* Branded Header */}
      <header
        className="py-5 px-6 shadow-md"
        style={{ background: `linear-gradient(135deg, ${primaryColor}, ${hexToRgba(primaryColor, 0.8)})` }}
      >
        <div className="max-w-[1600px] mx-auto flex items-center justify-between">
          <div className="flex items-center space-x-4">
            {branding.logoUrl && (
              <img src={branding.logoUrl} alt="" className="h-16 w-auto object-contain" data-testid="public-logo" />
            )}
            <div>
              <h1 className={`text-lg sm:text-2xl lg:text-3xl font-bold ${textMode === 'light' ? 'text-white' : 'text-slate-900'}`}>
                {branding.organizationName || 'Parking Availability'}
              </h1>
              <p className={`text-sm ${textMode === 'light' ? 'text-white/70' : 'text-slate-600'}`}>
                Real-time parking availability
              </p>
            </div>
          </div>
          <div className="hidden sm:flex items-center space-x-4 text-sm">
            <span className={`flex items-center space-x-1.5 ${textMode === 'light' ? 'text-white/80' : 'text-slate-600'}`}>
              <Car className="w-4 h-4" />
              <span className="font-bold">{filteredGarages.length}</span><span>Locations</span>
            </span>
            <span className={`flex items-center space-x-1.5 ${textMode === 'light' ? 'text-white/80' : 'text-slate-600'}`}>
              <Users className="w-4 h-4" />
              <span className="font-bold">{filteredGarages.reduce((s, g) => s + g.availableSpaces, 0)}</span><span>Available</span>
            </span>
          </div>
        </div>
      </header>

      {/* Toolbar: Search + Filters */}
      <div className="max-w-[1600px] mx-auto px-4 sm:px-6 pt-4 pb-2">
        <div className="flex flex-col sm:flex-row sm:items-center gap-3">
          <Input
            type="text" placeholder="Search locations..." value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)} className="sm:w-72 bg-white"
            data-testid="public-search-input"
          />
          <div className="flex items-center space-x-2 flex-nowrap overflow-x-auto pb-1 scrollbar-hide gap-y-2">
            <button
              onClick={() => setTypeFilter('all')}
              className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                typeFilter === 'all' ? 'text-white' : 'bg-white text-slate-600 border border-slate-200 hover:bg-slate-50'
              }`}
              style={typeFilter === 'all' ? { backgroundColor: primaryColor } : {}}
              data-testid="filter-all"
            >
              All ({garages.length})
            </button>
            <button
              onClick={() => setTypeFilter('garage')}
              className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors flex items-center space-x-1.5 ${
                typeFilter === 'garage' ? 'text-white' : 'bg-white text-slate-600 border border-slate-200 hover:bg-slate-50'
              }`}
              style={typeFilter === 'garage' ? { backgroundColor: primaryColor } : {}}
              data-testid="filter-garage"
            >
              <Building2 className="w-3.5 h-3.5" /><span>Garage ({garageCount})</span>
            </button>
            <button
              onClick={() => setTypeFilter('surface')}
              className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors flex items-center space-x-1.5 ${
                typeFilter === 'surface' ? 'text-white' : 'bg-white text-slate-600 border border-slate-200 hover:bg-slate-50'
              }`}
              style={typeFilter === 'surface' ? { backgroundColor: primaryColor } : {}}
              data-testid="filter-surface"
            >
              <ParkingSquare className="w-3.5 h-3.5" /><span>Surface ({surfaceCount})</span>
            </button>
            {/* Tag filters */}
            {allTags.length > 0 && (
              <>
                <div className="w-px h-5 bg-slate-300 mx-1 hidden sm:block" />
                <button
                  onClick={() => setTagFilter('all')}
                  className={`px-3 py-1.5 rounded-full text-xs font-medium transition-colors ${
                    tagFilter === 'all' ? 'text-white' : 'bg-white text-slate-500 border border-slate-200 hover:bg-slate-50'
                  }`}
                  style={tagFilter === 'all' ? { backgroundColor: primaryColor } : {}}
                  data-testid="tag-filter-all"
                >
                  All Tags
                </button>
                {allTags.map(tag => (
                  <button
                    key={tag}
                    onClick={() => setTagFilter(tag)}
                    className={`px-3 py-1.5 rounded-full text-xs font-medium transition-colors ${
                      tagFilter === tag ? 'text-white' : 'bg-white text-slate-500 border border-slate-200 hover:bg-slate-50'
                    }`}
                    style={tagFilter === tag ? { backgroundColor: primaryColor } : {}}
                    data-testid={`tag-filter-${tag.toLowerCase().replace(/\s+/g, '-')}`}
                  >
                    {tag}
                  </button>
                ))}
              </>
            )}
            {/* Divider before utility buttons */}
            <div className="w-px h-5 bg-slate-300 mx-1 hidden sm:block" />
            {/* Favorites filter */}
            <button
              onClick={() => setShowFavoritesOnly(prev => !prev)}
              className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors flex items-center space-x-1.5 ${
                showFavoritesOnly ? 'text-white' : 'bg-white text-slate-500 border border-slate-200 hover:bg-slate-50'
              }`}
              style={showFavoritesOnly ? { backgroundColor: '#ef4444' } : {}}
              data-testid="filter-favorites"
            >
              <Heart className={`w-3.5 h-3.5 ${showFavoritesOnly ? 'fill-white' : ''}`} />
              <span>Favorites{favorites.length > 0 ? ` (${favorites.length})` : ''}</span>
            </button>
            {/* Sort by nearest */}
            {userLocation && (
              <button
                onClick={() => setSortNearest(prev => !prev)}
                className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors flex items-center space-x-1.5 ${
                  sortNearest ? 'text-white' : 'bg-white text-slate-500 border border-slate-200 hover:bg-slate-50'
                }`}
                style={sortNearest ? { backgroundColor: primaryColor } : {}}
                data-testid="sort-nearest"
              >
                <ArrowDownNarrowWide className="w-3.5 h-3.5" />
                <span>Nearest First</span>
              </button>
            )}
            {/* Destinations toggle */}
            {projectDestinations.length > 0 && (
              <button
                onClick={() => setShowDestinations(prev => !prev)}
                className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors flex items-center space-x-1.5 ${
                  showDestinations ? 'text-white' : 'bg-white text-slate-500 border border-slate-200 hover:bg-slate-50'
                }`}
                style={showDestinations ? { backgroundColor: '#6366f1' } : {}}
                data-testid="toggle-destinations"
              >
                <Flag className={`w-3.5 h-3.5 ${showDestinations ? 'fill-white/30' : ''}`} />
                <span>Destinations</span>
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-[1600px] mx-auto px-4 sm:px-6 py-3">
        <Tabs value={activeView} onValueChange={setActiveView} className="w-full">
          <TabsList className="grid w-full grid-cols-2 max-w-xs mb-4">
            <TabsTrigger value="map" className="flex items-center space-x-2" data-testid="public-map-tab">
              <Map className="w-4 h-4" /><span>Map</span>
            </TabsTrigger>
            <TabsTrigger value="list" className="flex items-center space-x-2" data-testid="public-list-tab">
              <List className="w-4 h-4" /><span>List</span>
            </TabsTrigger>
          </TabsList>

          {/* MAP VIEW — responsive: side panel on desktop, bottom sheet on mobile */}
          <TabsContent value="map" className="mt-0">
            <div className="relative" style={{ height: '600px' }}>
              {/* Desktop: side-by-side */}
              <div className="hidden lg:flex gap-4 h-full">
                <div className="w-80 flex-shrink-0 bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden flex flex-col">
                  <div className="p-3 border-b border-slate-100">
                    <h3 className="text-sm font-semibold text-slate-700">{filteredGarages.length} Locations</h3>
                  </div>
                  <div className="flex-1 overflow-y-auto p-2 space-y-2">
                    {filteredGarages.map(g => <GarageListItem key={g.id} garage={g} />)}
                    {filteredGarages.length === 0 && (
                      <div className="text-center py-8 text-sm text-slate-500">No locations found</div>
                    )}
                  </div>
                </div>
                <div className="flex-1 rounded-xl overflow-hidden border border-slate-200 shadow-sm">
                  <MapView
                    garages={filteredGarages}
                    destinations={showDestinations ? projectDestinations : []}
                    onDirectionsClick={(g) => window.open(getDirectionsUrl(g), '_blank')}
                    onGarageClick={(g) => setSelectedGarage(g)}
                    onFindLocation={findUserLocation}
                    locationStatus={locationStatus}
                    userLocation={userLocation}
                    mapCenter={branding.mapCenter}
                    mapZoom={branding.mapZoom}
                    primaryColor={primaryColor}
                    hoveredGarageId={hoveredGarageId}
                    onReserveClick={showReservation ? (g) => setReservationGarage(g) : null}
                    favorites={favorites}
                    onToggleFavorite={toggleFavorite}
                  />
                </div>
              </div>

              {/* Mobile: full map + bottom sheet toggle */}
              <div className="lg:hidden h-full">
                <div className="h-full rounded-xl overflow-hidden border border-slate-200 shadow-sm">
                  <MapView
                    garages={filteredGarages}
                    destinations={showDestinations ? projectDestinations : []}
                    onDirectionsClick={(g) => window.open(getDirectionsUrl(g), '_blank')}
                    onGarageClick={(g) => setSelectedGarage(g)}
                    onFindLocation={findUserLocation}
                    locationStatus={locationStatus}
                    userLocation={userLocation}
                    mapCenter={branding.mapCenter}
                    mapZoom={branding.mapZoom}
                    primaryColor={primaryColor}
                    hoveredGarageId={hoveredGarageId}
                    onReserveClick={showReservation ? (g) => setReservationGarage(g) : null}
                    favorites={favorites}
                    onToggleFavorite={toggleFavorite}
                  />
                </div>
              </div>
            </div>
          </TabsContent>

          {/* LIST VIEW — card grid */}
          <TabsContent value="list" className="mt-0">
            <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4">
              {filteredGarages.map((garage) => {
                const hasLevels = garage.levels?.length > 0;
                const hasAttrs = garage.attributes?.length > 0;
                const isFav = favorites.includes(garage.id);
                return (
                  <div key={garage.id} className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden cursor-pointer hover:shadow-lg transition-shadow" onClick={() => setSelectedGarage(garage)}>
                    {garage.photoUrl && (
                      <div className="relative">
                        <img src={garage.photoUrl} alt={garage.name} className="w-full h-40 object-cover" />
                        <button
                          onClick={(e) => { e.stopPropagation(); toggleFavorite(garage.id); }}
                          className="absolute top-2 right-2 p-1.5 rounded-full bg-white/80 backdrop-blur-sm hover:bg-white transition-colors shadow-sm"
                          data-testid={`list-fav-btn-${garage.id}`}
                        >
                          <Heart className={`w-4 h-4 transition-colors ${isFav ? 'fill-red-500 text-red-500' : 'text-slate-400 hover:text-red-400'}`} />
                        </button>
                      </div>
                    )}
                    <div className="p-4 space-y-3">
                      <div className="flex items-start justify-between gap-2">
                        <div className="min-w-0 flex-1">
                          <div className="flex items-center gap-2">
                            <h3 className="text-base font-semibold text-slate-800 mb-1">{garage.name}</h3>
                            {!garage.photoUrl && (
                              <button
                                onClick={(e) => { e.stopPropagation(); toggleFavorite(garage.id); }}
                                className="p-1 rounded-full hover:bg-slate-100 transition-colors mb-1"
                                data-testid={`list-fav-btn-${garage.id}`}
                              >
                                <Heart className={`w-4 h-4 transition-colors ${isFav ? 'fill-red-500 text-red-500' : 'text-slate-300 hover:text-red-400'}`} />
                              </button>
                            )}
                          </div>
                          <div className="flex items-start text-xs text-slate-600">
                            <MapPin className="w-3.5 h-3.5 mr-1 flex-shrink-0 mt-0.5" />
                            <span className="line-clamp-2">{garage.address}</span>
                          </div>
                          {garage.distance != null && (
                            <div className="flex items-center gap-1 text-xs text-slate-500 mt-1">
                              <Navigation className="w-3 h-3" />
                              <span>{garage.distance < 0.1 ? '< 0.1' : garage.distance} mi away</span>
                            </div>
                          )}
                        </div>
                        <div className="flex flex-col items-end space-y-1 flex-shrink-0">
                          <Badge className={`${getAvailabilityColor(garage.availabilityStatus)} text-[10px]`}>{getAvailabilityText(garage.availabilityStatus)}</Badge>
                          {garage.locationType && (
                            <Badge className="bg-slate-100 text-slate-600 text-[10px]">{garage.locationType === 'garage' ? 'Garage' : 'Surface'}</Badge>
                          )}
                        </div>
                      </div>
                      <div className="bg-slate-50 rounded-lg p-3">
                        <div className="grid grid-cols-3 gap-2 text-center">
                          <div><div className="text-lg font-bold text-green-600">{garage.availableSpaces}</div><div className="text-[10px] text-slate-500">Available</div></div>
                          <div><div className="text-lg font-bold text-slate-800">{garage.totalSpaces}</div><div className="text-[10px] text-slate-500">Total</div></div>
                          <div><div className={`text-lg font-bold ${getBarTextColor(garage.utilizedPercent)}`}>{garage.utilizedPercent}%</div><div className="text-[10px] text-slate-500">Utilized</div></div>
                        </div>
                        <div className="mt-2 w-full bg-slate-200 rounded-full h-2">
                          <div className={`h-2 rounded-full ${getBarColor(garage.utilizedPercent)}`} style={{ width: `${Math.max(garage.utilizedPercent, 2)}%` }} />
                        </div>
                      </div>
                      {/* Walk times to destinations */}
                      {showDestinations && garageWalkTimes[garage.id]?.length > 0 && (
                        <div className="flex flex-wrap gap-1.5">
                          {garageWalkTimes[garage.id].map(wt => (
                            <span
                              key={wt.dest.id}
                              className="inline-flex items-center gap-1 text-xs bg-indigo-50 text-indigo-700 px-2 py-1 rounded-full"
                            >
                              <Footprints className="w-3 h-3" />
                              <span className="font-medium">{formatWalkTime(wt.walkMin)}</span>
                              <span className="text-indigo-500">to {wt.dest.name}</span>
                            </span>
                          ))}
                        </div>
                      )}
                      {(hasLevels || hasAttrs) && (
                        <details className="group" onClick={(e) => e.stopPropagation()}>
                          <summary className="flex items-center justify-between text-sm text-slate-600 py-1 cursor-pointer list-none [&::-webkit-details-marker]:hidden">
                            <span className="flex items-center space-x-1.5">
                              <Layers className="w-3.5 h-3.5" />
                              <span>{hasLevels ? `${garage.levels.length} levels` : ''}{hasLevels && hasAttrs ? ' · ' : ''}{hasAttrs ? `${garage.attributes.length} types` : ''}</span>
                            </span>
                            <ChevronDown className="w-4 h-4 transition-transform group-open:rotate-180" />
                          </summary>
                          <div className="space-y-1.5 mt-2">
                            {garage.levels?.map((l, i) => (
                              <div key={i}><div className="flex justify-between text-xs"><span className="text-slate-600">{l.name}</span><span className={`font-bold ${getBarTextColor(l.utilizedPercent)}`}>{l.availableSpaces}/{l.totalSpaces}</span></div>
                              <div className="w-full bg-slate-100 rounded-full h-1.5"><div className={`h-1.5 rounded-full ${getBarColor(l.utilizedPercent)}`} style={{ width: `${Math.max(l.utilizedPercent, 2)}%` }} /></div></div>
                            ))}
                            {garage.attributes?.map((a, i) => (
                              <div key={i}><div className="flex justify-between text-xs"><span className="text-slate-600">{a.name}</span><span className={`font-bold ${getBarTextColor(a.utilizedPercent)}`}>{a.availableSpaces}/{a.totalSpaces}</span></div>
                              <div className="w-full bg-slate-100 rounded-full h-1.5"><div className={`h-1.5 rounded-full ${getBarColor(a.utilizedPercent)}`} style={{ width: `${Math.max(a.utilizedPercent, 2)}%` }} /></div></div>
                            ))}
                          </div>
                        </details>
                      )}
                      {showReservation && (
                        <Button onClick={(e) => { e.stopPropagation(); setReservationGarage(garage); }} variant="outline" className="w-full text-sm border-2 font-semibold" style={{ borderColor: primaryColor, color: primaryColor }} data-testid={`reserve-btn-${garage.id}`}>
                          <CreditCard className="w-3.5 h-3.5 mr-1.5" />Pay For Reserved Parking
                        </Button>
                      )}
                      <Button onClick={(e) => { e.stopPropagation(); window.open(getDirectionsUrl(garage), '_blank'); }} className="w-full text-white text-sm" style={{ backgroundColor: primaryColor }}>
                        <Navigation className="w-3.5 h-3.5 mr-1" />Directions
                      </Button>
                    </div>
                  </div>
                );
              })}
              {filteredGarages.length === 0 && (
                <div className="col-span-full text-center py-12">
                  <Car className="w-16 h-16 mx-auto mb-4 text-slate-400" />
                  <h3 className="text-lg font-medium text-slate-600">No locations found</h3>
                </div>
              )}
            </div>
          </TabsContent>
        </Tabs>
      </div>

      <LocationDetailModal
        garage={selectedGarage}
        open={!!selectedGarage}
        onClose={() => setSelectedGarage(null)}
        primaryColor={primaryColor}
        onReserveClick={showReservation ? (g) => { setSelectedGarage(null); setReservationGarage(g); } : null}
      />
      <ReservationModal
        garage={reservationGarage}
        open={!!reservationGarage}
        onClose={() => setReservationGarage(null)}
        primaryColor={primaryColor}
      />

      {/* Mobile: fixed toggle button + bottom sheet (outside map DOM) */}
      {!mobilePanelOpen && (
        <div className="lg:hidden fixed bottom-0 left-0 right-0 z-[9998] px-4 pb-4 pt-2 bg-gradient-to-t from-white via-white/90 to-transparent pointer-events-none">
          <button
            onClick={() => setMobilePanelOpen(true)}
            className="pointer-events-auto w-full bg-white shadow-lg border border-slate-200 rounded-xl px-5 py-3.5 flex items-center justify-center gap-2 text-sm font-bold text-slate-700 active:scale-[0.98] transition-transform"
            style={{ boxShadow: '0 -2px 16px rgba(0,0,0,.1)' }}
            data-testid="mobile-locations-toggle"
          >
            <List className="w-4 h-4" />
            {filteredGarages.length} Locations
            <ChevronUp className="w-4 h-4" />
          </button>
        </div>
      )}
      {mobilePanelOpen && (
        <div className="lg:hidden fixed inset-0 z-[10001]" data-testid="mobile-panel">
          <div className="absolute inset-0 bg-black/40" onClick={() => setMobilePanelOpen(false)} />
          <div className="absolute bottom-0 left-0 right-0 bg-white rounded-t-2xl shadow-2xl max-h-[75vh] flex flex-col">
            <div className="pt-2"><div className="w-10 h-1 bg-slate-300 rounded-full mx-auto" /></div>
            <div className="flex items-center justify-between px-4 py-3 border-b border-slate-100 flex-shrink-0">
              <div>
                <h3 className="text-sm font-bold text-slate-800">{filteredGarages.length} Locations</h3>
                <span className="text-xs text-slate-500">{filteredGarages.reduce((s, g) => s + g.availableSpaces, 0)} spaces available</span>
              </div>
              <button onClick={() => setMobilePanelOpen(false)} className="p-1.5 rounded-lg hover:bg-slate-100 text-slate-500" data-testid="mobile-panel-close">
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="flex-1 overflow-y-auto p-3 space-y-2">
              {filteredGarages.map(g => <GarageListItem key={g.id} garage={g} />)}
              {filteredGarages.length === 0 && <div className="text-center py-8 text-sm text-slate-500">No locations found</div>}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
